/**
 * @fileoverview deployment-edges.ts — single source of contracts
 *               for the edge tier over the deployment IO node.
 * @module apps/network/global/io/edges/deployment/deployment-edges
 *
 * Pure TS. Holds every edge's KEY + Context interface +
 * edge-specific helper type. Each edge's `.svelte` re-exports
 * the binding(s) it needs from here, never re-declares — the
 * one-contract-two-payoffs discipline as the node tier.
 *
 * Imports ONLY from `../../nodes/deployment/deployment-io.js`
 * (the node's TS contract surface) — NEVER from
 * `@liquidefy/network-deployment` directly (that boundary stays
 * at the node). Zero DOM, zero Svelte.
 */

import type {
  DeployTargetRef,
  DeploymentStreamEvent,
  LifecyclePhase,
  LiquidefyTopology,
  SupplyRunSpec,
  TopologyValidation,
} from "../../nodes/deployment/deployment-io.js";

// ─── LiveTimelineEdge — the dashboard feed (E1) ─────────────────

/** Composable filter over the unified-event stream. Empty fields ⇒
 *  no filter on that dimension. */
export interface LiveTimelineFilter {
  readonly origin?: string;
  readonly op?: string;
  /** Arc prefix, e.g. `"release."`, `"db."`, `"rollback."`. */
  readonly arcPrefix?: string;
}

export const LIVE_TIMELINE_EDGE_KEY =
  "deployment-live-timeline-edge" as const;

export interface LiveTimelineEdgeContext {
  /** Stream events, filtered by `filter`. */
  readonly events: readonly DeploymentStreamEvent[];
  /** Deterministic `origin/code=count` digest from Observability. */
  readonly digest: readonly string[];
  /** The 8 lifecycle phases (the v1.9.0 truth from LifecycleIO). */
  readonly phases: readonly LifecyclePhase[];
  readonly completeCount: number;
  /** Most-recent event per arc prefix
   *  (`release. | traffic. | db. | storage. | cache. | queue. |
   *  telemetry. | rollback.`) — `null` if none seen yet. */
  readonly latestByArc: Readonly<
    Record<string, DeploymentStreamEvent | null>
  >;
  readonly filter: LiveTimelineFilter;
  setFilter(filter: LiveTimelineFilter): void;
  clearFilter(): void;
}

// ─── TopologyTruthEdge — declared vs bound vs stale (E1) ────────

export const TOPOLOGY_TRUTH_EDGE_KEY =
  "deployment-topology-truth-edge" as const;

export interface TopologyTruthEdgeContext {
  /** The declared topology (from TopologyIO). */
  readonly topology: LiquidefyTopology | null;
  /** The last validation result (set by `validate(...)`). */
  readonly lastValidation: TopologyValidation | null;
  readonly lastError: Error | null;
  /** Stream events filtered to `binding.*` ops. */
  readonly bindingsHistory: readonly DeploymentStreamEvent[];
  /** Stream events filtered to `credentials.*` ops. */
  readonly credentialsHistory: readonly DeploymentStreamEvent[];
  /** Validate any candidate (defaults to the current topology).
   *  Throws (and records `lastError`) if no current topology and
   *  no candidate is supplied. */
  validate(candidate?: LiquidefyTopology): TopologyValidation;
  /** Diff the current topology against `against`. Proxies the
   *  node's `topology.diff`. */
  diff(against: LiquidefyTopology): unknown;
  /** Re-run the substrate seam's conformance verdict. */
  reverifyBinding(seam: string): Promise<unknown>;
  /** Trigger a credential rotation. */
  rotateCredential(
    ref: string,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}

// ─── DeployRunEdge — the canonical "do a deploy" (E2) ────────────

/** The unified DeployRun state machine. */
export type DeployRunStage =
  | "idle"
  | "previewing"
  | "gated"
  | "awaiting-approval"
  | "running"
  | "cutover"
  | "succeeded"
  | "failed";

export const DEPLOY_RUN_EDGE_KEY = "deployment-deploy-run-edge" as const;

export interface DeployRunEdgeContext {
  readonly stage: DeployRunStage;
  readonly currentSpec: SupplyRunSpec | null;
  /** The latest preview diff (opaque). */
  readonly currentDiff: unknown;
  /** The latest gate verdict (opaque). */
  readonly gateVerdict: unknown;
  readonly error: Error | null;
  /** True iff ANY underlying portal action is in flight. */
  readonly inFlight: boolean;
  /** Pending approval-request events (filtered from the stream). */
  readonly pendingApprovals: readonly DeploymentStreamEvent[];
  /** Preview only — stage: idle/previewing → gated. */
  prepare(spec: SupplyRunSpec): Promise<unknown>;
  /** Preview (if needed) + gate — stage advances to gated. */
  gate(spec: SupplyRunSpec): Promise<unknown>;
  /** Full path: preview → gate → trigger — culminates in
   *  `succeeded` (or `failed` on any step). */
  run(spec: SupplyRunSpec): Promise<unknown>;
  /** Approve or deny a pending approval. */
  decideApproval(
    approvalId: string,
    decision: "approve" | "deny",
    note?: string,
  ): Promise<unknown>;
  /** Cutover an already-triggered deploy. Stage: cutover → succeeded. */
  cutover(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  /** Surface a typed error and reset stage to idle. Does NOT cancel
   *  inflight daemon work (the daemon's concern). */
  abort(): void;
}

// ─── ReleaseLifecycleEdge — a release through its phases (E2) ────

/** The m-arc release lifecycle phase. */
export type ReleasePhase =
  | "idle"
  | "rolling"
  | "cutover"
  | "drained"
  | "rolledBack"
  | "failed";

export const RELEASE_LIFECYCLE_EDGE_KEY =
  "deployment-release-lifecycle-edge" as const;

export interface ReleaseLifecycleEdgeContext {
  readonly phase: ReleasePhase;
  readonly target: DeployTargetRef | null;
  readonly lastError: Error | null;
  readonly inFlight: boolean;
  /** Stream events for `release.*` ops (the release's own slice). */
  readonly provenance: readonly DeploymentStreamEvent[];
  start(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  cutover(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  drain(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  rollback(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
  /** Composed one-button lifecycle: start → cutover → drain.
   *  Phases: rolling → cutover → drained (or failed). */
  withCutover(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown>;
}
